import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  NgZone,
  OnInit,
  ViewChild,
  inject,
} from "@angular/core";

import { NgxLoadingModule } from "ngx-loading";
import { OnChanges, TypedChanges } from "src/shared/utils";

import { cacheClear$ } from "../../services/cache-clear.service";

const errorCache = new Set<string>();
cacheClear$.subscribe(() => errorCache.clear());

@Component({
  selector: "app-safe-image",
  templateUrl: "./safe-image.component.html",
  styleUrls: ["./safe-image.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    "[style.width.px]": "width",
    "[style.height.px]": "height",
    "[class.fluid]": "fluid",
  },
  imports: [NgxLoadingModule],
})
export class SafeImageComponent implements OnInit, OnChanges {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);

  @Input({ required: true }) src?: string;

  @Input() default = "/assets/images/generic_object.svg";
  @Input() imgClass = "";

  @Input() @HostBinding("style.max-width") maxWidth: string;
  @Input() @HostBinding("style.max-height") maxHeight: string;
  @Input() @HostBinding("style.min-width") minWidth: string;
  @Input() @HostBinding("style.min-height") minHeight: string;
  @Input() width: number;
  @Input() height: number;

  /**
   * Fluid mode: the image fills the host width and keeps its natural aspect
   * ratio (height is computed from the image), instead of being letterboxed
   * inside a fixed box. The box reflows to the image's height once it loads.
   */
  @Input() fluid = false;
  @Input({ required: true }) alt: string;
  @Input() fetchpriority?: "high" | "low" | "auto";

  loaded: boolean | null = null;

  @ViewChild("imgEl", { static: true }) imgElRef: ElementRef<HTMLImageElement>;

  ngOnInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.imgElRef.nativeElement.addEventListener("load", this.onLoad);
      this.imgElRef.nativeElement.addEventListener("error", this.onError);
    });
  }

  ngOnChanges(changes: TypedChanges<SafeImageComponent>): void {
    if (changes.src) {
      this.loaded = !this.src || errorCache.has(this.src) ? false : null;
    }
  }

  private onError = () => {
    if (this.src == null) return;
    this.loaded = false;
    errorCache.add(this.src);
    this.cdr.detectChanges();
  };

  private onLoad = () => {
    if (this.src == null) return;
    if (this.loaded === false) return;
    this.loaded = true;
    errorCache.delete(this.src);
    this.cdr.detectChanges();
  };
}
